import ENDPOINTS from 'tradera-constants/endpoints';
import {
    axiosWithTokenRefresh,
    checkResponseVersion,
    finalizeResponse,
    handleError,
    utilizeCancelToken,
    axiosConfigs,
    logError
} from 'tradera-utils/api';
import initData from 'init-data';
import { toLocalizedUrl } from 'tradera-utils/url';
import { getLanguage } from 'tradera-apps/syi/script/app_react/utils/language';
import { isServer } from 'tradera-utils/nextjs';

const ensureClientOnly = () => {
    // Cancel tokens would be shared per server instance so ensure we are not on server.
    if (isServer) {
        throw new Error('This HTTP client is only safe to use on frontend');
    }
};

// Prevents URL:s that begins with // when that was not intended.
const getSafeUrl = (baseUrl, url) => {
    if (baseUrl.endsWith('/') && url.startsWith('/')) {
        return baseUrl + url.substring(1);
    }
    return baseUrl + url;
};

const getNet5UrlIfEnabled = url => {
    const matches = url.match(/^\/(\w+)\//);
    if (matches.length == 2) {
        const endpointName = matches[1];

        if (initData?.featureSwitches?.[`webapi-${endpointName}-net5`]) {
            return url.replace(endpointName, `${endpointName}-net5`);
        }
    }

    return url;
};

const httpClient = (baseUrl, shouldLocalizeUrl, isWebApiClient) => {
    const axiosWrapper = (url, httpClientConfig, axiosCaller) => {
        let { cancelTokenId, ...axiosConfig } = httpClientConfig;
        const version = initData.version;
        if (cancelTokenId) {
            const { cancel, cancelToken } = utilizeCancelToken(cancelTokenId);
            // eslint-disable-next-line better-mutation/no-mutation
            axiosConfig.cancelToken = cancelToken;
            if (cancel) {
                cancel();
            }
        }
        const isWebApiNet5FromFrontendEnabled =
            initData?.featureSwitches?.['webapi-net5-from-frontend'];
        const net5Url =
            isWebApiClient && isWebApiNet5FromFrontendEnabled
                ? getNet5UrlIfEnabled(url)
                : url;
        const safeUrl = getSafeUrl(baseUrl, net5Url);
        const localizedUrl = shouldLocalizeUrl
            ? toLocalizedUrl(safeUrl, getLanguage())
            : safeUrl;
        return axiosCaller(axiosWithTokenRefresh(), localizedUrl, axiosConfig)
            .then(
                version ? checkResponseVersion(version) : response => response
            )
            .then(finalizeResponse())
            .catch(handleError())
            .catch(error => {
                if (isWebApiClient && isWebApiNet5FromFrontendEnabled) {
                    logError(error, {
                        tags: {
                            webapiNet5: net5Url !== url
                        }
                    });
                }
                throw error;
            });
    };

    return {
        get: (url, httpClientConfig = axiosConfigs.authenticated) => {
            ensureClientOnly();
            return axiosWrapper(url, httpClientConfig, (axios, url, config) =>
                axios.get(url, config)
            );
        },
        post: (url, payload, httpClientConfig = axiosConfigs.authenticated) => {
            ensureClientOnly();
            return axiosWrapper(url, httpClientConfig, (axios, url, config) =>
                axios.post(url, payload, config)
            );
        },
        put: (url, payload, httpClientConfig = axiosConfigs.authenticated) => {
            ensureClientOnly();
            return axiosWrapper(url, httpClientConfig, (axios, url, config) =>
                axios.put(url, payload, config)
            );
        }
    };
};

const trpcClient = endpoint => {
    const client = httpClient(`/api/${endpoint}`, false);
    const unpackResponse = ({ data }) => data;
    return {
        command: (commandName, payload, httpClientConfig) =>
            client
                .post(`/commands/${commandName}`, payload, httpClientConfig)
                .then(unpackResponse),
        query: (queryName, httpClientConfig) =>
            client
                .get(`/queries/${queryName}`, httpClientConfig)
                .then(unpackResponse)
    };
};

export const defaultClient = httpClient('');
export const touchWebClient = httpClient('/', true);
export const webApiClient = httpClient(ENDPOINTS.WEB_API, false, true);
export const cmsApiClient = httpClient(ENDPOINTS.CMS_API);
export const marketingApiClient = httpClient(ENDPOINTS.MARKETING_PUBLIC_API);
export const searchSuggestionsClient = httpClient(ENDPOINTS.SEARCH_SUGGESTIONS);
export const memberIdentificationClient = trpcClient('member-identification');
export const translationDynamicApiClient = trpcClient(
    'translation-dynamic-api'
);
export const shippingRecommendationsClient = trpcClient(
    'shipping-recommendations'
);
